import { MonikerKind, type CanonicalDocument, type DocBlock, type DocInline } from './types'
import { normalizeMonikerKey } from './monikerKey'

/**
 * One variable found in the live editor document. A trimmed-down `DiscoveredMoniker` — the source
 * location the server records is not needed here, since this exists only to build a payload
 * skeleton, not to report clashes.
 */
export interface CollectedMoniker {
  rawName: string
  normalizedKey: string
  kind: MonikerKind
}

/**
 * Every variable in a CanonicalDocument, in document order and deduplicated by normalized key.
 *
 * A TS port of `CanonicalMonikerScanner.Scan` (lib/src/Services/CanonicalMonikerScanner.cs), and
 * deliberately a port rather than a server round-trip: the editor needs this on every keystroke to
 * keep the payload panel in step with the document, and asking the API for it would be a request
 * per edit for something fully derivable from state the client already holds.
 *
 * **It has to stay in step with that scanner.** A block the server walks and this does not means
 * the payload panel silently omits a variable that the saved template really has — which is the
 * whole class of bug this function exists to fix. The two walk the same node types in the same
 * order; `collectMonikers.test.ts` pins that against the shared golden fixture.
 */
export function collectMonikers(doc: CanonicalDocument): CollectedMoniker[] {
  const found: CollectedMoniker[] = []
  const seen = new Set<string>()

  const add = (rawName: string, normalizedKey: string, kind: MonikerKind) => {
    // MonikerKey.Discover's rule: loop counters ($index, $first …) are engine-supplied, not payload
    // fields; an indexed path (items[0].name) is reported as a Loop on its base collection.
    if (rawName.trim().startsWith('$')) return
    const bracket = rawName.indexOf('[')
    if (bracket >= 0) {
      rawName = rawName.slice(0, bracket).trim()
      normalizedKey = normalizeMonikerKey(rawName)
      kind = MonikerKind.Loop
    }
    if (!normalizedKey || seen.has(normalizedKey)) return
    seen.add(normalizedKey)
    found.push({ rawName, normalizedKey, kind })
  }

  const walkInlines = (inlines: DocInline[]) => {
    for (const inline of inlines) {
      if (inline.$type === 'placeholder') add(inline.rawName, inline.normalizedKey, MonikerKind.Scalar)
      else if (inline.$type === 'conditionalRun') {
        // Condition first, then its branches — the scanner's order.
        add(inline.rawName, inline.normalizedKey, MonikerKind.Conditional)
        walkInlines(inline.inlines)
        if (inline.elseInlines) walkInlines(inline.elseInlines)
      }
      else if (inline.$type === 'loopRun') {
        // The collection, then the repeated inlines — as the block loop.
        add(inline.rawName, inline.normalizedKey, MonikerKind.Loop)
        walkInlines(inline.inlines)
      }
    }
  }

  const walkBlocks = (blocks: DocBlock[]) => {
    for (const block of blocks) {
      switch (block.$type) {
        case 'paragraph':
        case 'heading':
          walkInlines(block.inlines)
          break
        case 'list':
          for (const item of block.items) walkBlocks(item.content)
          break
        case 'table':
          // A column loop's collection first (the scanner's order), then every cell.
          for (const row of block.rows)
            for (const cell of row.cells)
              if (cell.columnLoop) add(cell.columnLoop.rawName, cell.columnLoop.normalizedKey, MonikerKind.Loop)
          for (const row of block.rows) for (const cell of row.cells) walkBlocks(cell.content)
          break
        case 'textBox':
        case 'toc':
          walkBlocks(block.content)
          break
        case 'image':
          // An image whose alt text was a {{moniker}} is a variable too — the resolver swaps in
          // the payload's URL or data URI at render time.
          if (block.sourceMoniker) add(block.sourceMoniker, block.sourceMoniker, MonikerKind.Scalar)
          break
        case 'loop':
          add(block.rawName, block.normalizedKey, MonikerKind.Loop)
          walkBlocks(block.body)
          break
        case 'conditional':
          add(block.rawName, block.normalizedKey, MonikerKind.Conditional)
          walkBlocks(block.body)
          if (block.elseBody) walkBlocks(block.elseBody)
          break
      }
    }
  }

  for (const section of doc.sections) {
    walkBlocks(section.body)
    // Header and footer content is rendered and resolved like any other content, so a variable
    // living only in a letterhead still needs a payload key.
    for (const hf of [section.header, section.footer, section.firstPageHeader,
                      section.firstPageFooter, section.evenPageHeader, section.evenPageFooter])
      if (hf) walkBlocks(hf.content)
  }
  for (const note of [...doc.footnotes, ...doc.endnotes]) walkBlocks(note.content)

  return found
}
